<template>
  <DashboardLayout :lastUpdated="lastUpdated">
    <div slot="navbarFilters" class="issues-navbar-filters">
      <div class="ui multiple dropdown issues-navbar-dropdown" ref="filterSA">
        <input type="hidden" name="filter">
        <i class="users icon"></i>
        <span class="text issues-navbar-dd-summary">S.Accounts: All</span>
        <div class="menu">
          <div class="header">Seller Accounts</div>
          <div class="scrolling menu">
            <div v-for="sa in (sellerAccountsList || [])"
                 :key="sa.sellerId"
                 class="item"
                 :data-value="sa.sellerId">
              <i :class="(sa.region === 'NA' ? 'olive' : 'teal') + ' circle compact icon'"></i>{{ sa.accountNickName }}
            </div>
          </div>
        </div>
      </div>
      <div class="ui multiple dropdown issues-navbar-dropdown" ref="filterC">
        <input type="hidden" name="filter">
        <i class="globe icon"></i>
        <span class="text issues-navbar-dd-summary">Marketplaces: All</span>
        <div class="menu">
          <div class="header">Marketplaces</div>
          <div class="scrolling menu">
            <div v-for="c in filterCountriesForDropdown"
                 :key="c"
                 :class="c === 's' ? 'divider' : 'item'"
                 :data-value="c">
              <i v-if="c !== 's'" :class="c.toLowerCase().replace('uk','gb') + ' flag'"></i>{{ c !== 's' ? c : '' }}
            </div>
          </div>
        </div>
      </div>
      <div class="ui dropdown issues-navbar-dropdown" ref="filterDate">
        <input type="hidden" name="daterange">
        <i class="calendar icon"></i>
        <span class="text">Date Range: {{ dateRangeLabel }}</span>
        <div class="menu">
          <div class="item" data-value="today">Today</div>
          <div class="item" data-value="7d">Last 7 Days</div>
          <div class="item" data-value="30d">Last 30 Days</div>
          <div class="item" data-value="90d">Last 90 Days</div>
          <div class="item" data-value="all">All Time</div>
          <div class="item" data-value="custom">Custom range…</div>
        </div>
      </div>
      <form v-if="showCustomDatePicker" class="issues-custom-date-range" @submit.prevent="applyCustomDateRange">
        <input
          type="date"
          v-model="customDraftStart"
          :min="customDateMin"
          :max="customDraftEnd || customDateMax"
          aria-label="Start date"
          required
        />
        <span class="issues-custom-date-sep">–</span>
        <input
          type="date"
          v-model="customDraftEnd"
          :min="customDraftStart || customDateMin"
          :max="customDateMax"
          aria-label="End date"
          required
        />
        <button type="submit" class="issues-custom-date-btn primary" :disabled="!customDraftValid">Apply</button>
        <button type="button" class="issues-custom-date-btn" @click="cancelCustomDateRange">Cancel</button>
        <span class="issues-custom-date-hint">Last 90 days only</span>
      </form>
    </div>

    <div class="issues-dashboard">
        <div class="issues-main-layout">
          <!-- Alert Categories Sidebar -->
          <AlertCategoriesSidebar
            :alertCounts="wdAlertCounts"
            :activeCategory="activeCategory"
            @select="scrollToWatchdog"
          />

          <!-- Main Content -->
          <div class="issues-main-content">
            <!-- Loading State -->
            <div v-if="loading" class="issues-loading-state">
              <i class="spinner loading icon"></i>
              <p>Loading alerts…</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="issues-error-state">
              <i class="exclamation triangle icon"></i>
              <h3>Failed to load alerts</h3>
              <p>{{ error }}</p>
              <button class="issues-retry-btn" @click="fetchWatchdogsData">Retry</button>
            </div>

          <!-- Category Detail Views -->
          <div v-else id='wdCont' class="wide" key="1">
            <div class="issues-category-list-wrapper" id="context">
              <IssuesCategoryDetail
                v-for="i in displayedWatchdogs"
                v-if="issuesWorldStore && issuesWorldStore.watchDogs[i]"
                :key="i"
                :wdtype="i"
                :wd="issuesWorldStore.watchDogs[i]"
                :filterByMarket="filterByMarket"
                :filterBySellAcc="filterBySellAcc"
                :dateRange="'all'"
                :issues-list-tab="issuesListTab"
                :resolved-hidden="issuesResolvedHidden"
                :marketplace-filter-options="marketplaceColumnOptions"
                :highlighted-alert-id="highlightedAlertId"
                :items-meta="issuesItemsMeta"
                :fee-direction="issuesFeeDirection"
                :items-loading="issuesItemsLoading"
                @issues-tab-change="onIssuesTabChange"
                @alert-counts-delta="onAlertCountsDelta"
                @issues-page-change="onIssuesPageChange"
                @issues-search-change="onIssuesSearchChange"
                @issues-fee-direction-change="onIssuesFeeDirectionChange"
                @issues-summary-refresh-request="onIssuesSummaryRefreshRequest"
                @filter-market="onMarketplaceColumnFilter"
                :alert-thresholds-api-base-url="alertThresholdsApiBaseUrl"
                :stock-alert-thresholds-api-base-url="stockAlertThresholdsApiBaseUrl"
                :sales-volume-thresholds-api-base-url="salesVolumeThresholdsApiBaseUrl"
                :issue-products="issueProductsMap"
              />
            </div>
            </div>

            <div id="darkbackgr" style="position:fixed; top:0; height:100vh; width:100vw; background-color:rgba(0,0,0,.6); pointer-events:none; z-index:11; display:none;"></div>
          </div>
        </div>
    </div>
  </DashboardLayout>
</template>

<script>
import $ from 'jquery'
import { extractV1SellersItems, mapV1SellersToFilterAccounts, extractMarketplaceCodesFromV1SellersBody, buildMarketplaceFilterDropdownItems, MARKETPLACE_FILTER_COUNTRIES_ALL } from './utils/sellerAccountsFromV1Api.js'
import {
  filterIssuesItemsGlobally,
  isIssuesActiveTabItem,
  isIssuesResolvedTabItem
} from './utils/issuesAlertFilters.js'
import {
  runIssuesSummaryFetchCore,
  fetchSummaryItemsForTypes,
  computeWatchdogTypesForItemsFetch,
  refreshIssuesSummaryQuiet
} from './utils/issuesSummaryFetch.js'
import { createRequestGate } from './utils/requestGate.js'
import { watchdogTypeFromApi } from './utils/watchdogSummaryApi.js'
import {
  alertIdFromQuery,
  categoryFromAlertEmailType,
  itemMatchesAlertId
} from './utils/alertEmailDeepLink.js'
import { normalizeMarketplaceCode } from './utils/amazonMarketplaceUrls.js'
import './components/issues/styles/IssuesDashboard.css'
import DashboardLayout from './components/buybox/DashboardLayout.vue'
import AlertCategoriesSidebar from './components/issues/AlertCategoriesSidebar.vue'
import IssuesCategoryDetail from './components/issues/IssuesCategoryDetail.vue'

const ISSUES_URL_KEYS = ['category', 'marketplaces', 'sellerAccounts', 'dateRange', 'startDate', 'endDate', 'tab', 'alertId']

/** Custom date range may reach back at most this many days (API enforces the same). */
const CUSTOM_RANGE_MAX_DAYS = 90

function toIsoDate (d) {
  var m = d.getMonth() + 1
  var day = d.getDate()
  return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day
}

function isoDateDaysAgo (days) {
  var d = new Date()
  d.setDate(d.getDate() - days)
  return toIsoDate(d)
}

/** Valid YYYY-MM-DD pair, start <= end, inside the last CUSTOM_RANGE_MAX_DAYS days. */
function isValidCustomRange (start, end) {
  var re = /^\d{4}-\d{2}-\d{2}$/
  if (!re.test(start || '') || !re.test(end || '')) return false
  return start <= end && start >= isoDateDaysAgo(CUSTOM_RANGE_MAX_DAYS) && end <= isoDateDaysAgo(0)
}

function formatShortDate (iso) {
  var parts = String(iso).split('-')
  var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function shallowEqualIssuesQuery (next, routeQuery) {
  var a = next || {}
  var b = routeQuery || {}
  for (var i = 0; i < ISSUES_URL_KEYS.length; i++) {
    var k = ISSUES_URL_KEYS[i]
    var nv = a[k] != null ? String(a[k]) : ''
    var rv = b[k] != null ? String(b[k]) : ''
    if (nv !== rv) return false
  }
  return true
}

/** True when only `category` differs between two issue route queries (other keys normalized as strings). */
function categoryQueryOnlyChange (prevQ, nextQ) {
  prevQ = prevQ || {}
  nextQ = nextQ || {}
  var keys = ['marketplaces', 'sellerAccounts', 'dateRange', 'startDate', 'endDate', 'tab', 'alertId']
  for (var i = 0; i < keys.length; i++) {
    var k = keys[i]
    var pv = prevQ[k] != null ? String(prevQ[k]) : ''
    var nv = nextQ[k] != null ? String(nextQ[k]) : ''
    if (pv !== nv) return false
  }
  var pc = prevQ.category != null ? String(prevQ.category) : ''
  var nc = nextQ.category != null ? String(nextQ.category) : ''
  return pc !== nc
}

/** Issues sits under `<transition>` / router; `$parent` may not be IndexPage. */
function resolveIssuesHost (vm) {
  var p = vm.$parent
  while (p) {
    if (p.wdList && Array.isArray(p.wdList) && p.worldStore) return p
    p = p.$parent
  }
  return null
}

export default {
  name: 'IssuesPage',
  components: {
    DashboardLayout,
    AlertCategoriesSidebar,
    IssuesCategoryDetail
  },
  data: function () {
    return {
      loading: false,
      error: null,
      lastUpdated: 'just now',
      lastUpdateTime: null,
      timerInterval: null,
      sortListByImportance: true,
      activeCategory: 'lostBuybox',
      filterByMarket: [],
      filterBySellAcc: [],
      filterCountriesAll: MARKETPLACE_FILTER_COUNTRIES_ALL,
      filterUserCountries: [],
      filterShowSASelector: false,
      dateRange: 'all',
      /** YYYY-MM-DD bounds used when dateRange === 'custom'. */
      customDateStart: '',
      customDateEnd: '',
      showCustomDatePicker: false,
      customDraftStart: '',
      customDraftEnd: '',
      /** FBA Fee Change list: '' | 'increase' | 'decrease'. */
      issuesFeeDirection: '',
      alertThresholdsApiBaseUrl: '/api/v1/watchdogs',
      stockAlertThresholdsApiBaseUrl: '/api/v1/stock-alert-thresholds',
      salesVolumeThresholdsApiBaseUrl: '/api/v1/sales-volume-alert-thresholds',
      /** `null` until first GET /api/v1/sellers completes; then array (possibly empty). */
      issuesV1SellerRows: null,
      /** Marketplace codes derived from GET /api/v1/sellers body (all seller-linked regions). */
      issuesSellerMarketplaceCodes: [],
      _issuesV1SellersPromise: null,
      issuesListTab: 'active',
      /** Whale catalogs: API sets meta.resolved_hidden — hide Resolved tab. */
      issuesResolvedHidden: false,
      issuesListPage: 1,
      issuesSearchQuery: '',
      issuesItemsMeta: null,
      issuesItemsLoading: false,
      issuesFocusPending: false,
      _applyingIssuesRouteQuery: false,
      _pendingIssuesScrollCategory: null,
      /** From GET /api/v1/watchdogs/summary/alert_counts — { type: { red, yellow } } */
      summaryAlertCounts: {},
      /** Product rows from summary/items, keyed by catalog id (string) */
      issueProductsMap: {},
      /** Avoid starting a second summary fetch while alert_counts / item scheduling is in progress. */
      _issuesSummaryFetchInFlight: false,
      _issuesFetchDebounce: null,
      _issuesItemsFetchDebounce: null,
      highlightedAlertId: ''
    }
  },
  computed: {
    issuesHost: function () {
      return resolveIssuesHost(this)
    },
    issuesWorldStore: function () {
      var h = this.issuesHost
      return h ? h.worldStore : null
    },
    sellerAccountsList: function () {
      var v1 = this.issuesV1SellerRows
      if (v1 != null && v1.length > 0) {
        return v1.map(function (r) {
          return { sellerId: r.id, accountNickName: r.name, region: r.region || 'NA' }
        })
      }
      var sa = this.issuesWorldStore && this.issuesWorldStore.sellerAccounts
      return sa ? Object.values(sa) : []
    },
    /** Marketplaces tied to this account (catalog + connected sellers). */
    filterCountriesForDropdown: function () {
      var allowed = this.filterUserCountries || []
      /** Issues often load via summary APIs with sparse `worldStore.products` — keep dropdown usable. */
      if (allowed.length === 0) {
        return buildMarketplaceFilterDropdownItems(
          this.filterCountriesAll.filter(function (c) { return c !== 's' }),
          this.filterCountriesAll
        )
      }
      return buildMarketplaceFilterDropdownItems(allowed, this.filterCountriesAll)
    },
    customDateMin: function () {
      return isoDateDaysAgo(CUSTOM_RANGE_MAX_DAYS)
    },
    customDateMax: function () {
      return isoDateDaysAgo(0)
    },
    customDraftValid: function () {
      return isValidCustomRange(this.customDraftStart, this.customDraftEnd)
    },
    dateRangeLabel: function () {
      if (this.dateRange === 'custom' && this.customDateStart && this.customDateEnd) {
        return formatShortDate(this.customDateStart) + ' – ' + formatShortDate(this.customDateEnd)
      }
      var labels = { today: 'Today', '7d': 'Last 7 Days', '30d': 'Last 30 Days', '90d': 'Last 90 Days', all: 'All Time', 'all time': 'All Time' }
      return labels[this.dateRange] || 'All Time'
    },
    marketplacesFilterLabel: function () {
      var m = this.filterByMarket
      if (!m || m.length === 0) return 'All'
      if (m.length === 1) return m[0]
      return m.length + ' selected'
    },
    sellerAccountsFilterLabel: function () {
      var s = this.filterBySellAcc
      if (!s || s.length === 0) return 'All'
      if (s.length === 1) {
        var list = this.sellerAccountsList || []
        for (var i = 0; i < list.length; i++) {
          if (String(list[i].sellerId) === String(s[0])) return list[i].accountNickName || String(s[0])
        }
        return String(s[0])
      }
      return s.length + ' selected'
    },
    wdListSorted: function () {
      var host = this.issuesHost
      if (!host || !host.wdList || !this.sortListByImportance || !host.worldStore) {
        return (host && host.wdList) || []
      }

      var hasAlert = []
      var noAlert = []
      var wd = host.worldStore.watchDogs
      var products = host.worldStore.products || {}
      var fm = this.filterByMarket || []
      var fs = this.filterBySellAcc || []
      /** Summary APIs apply `time_range` server-side; do not re-filter by date here. */
      var dr = 'all'

      for (var i = 0; i < host.wdList.length; i++) {
        var wdName = host.wdList[i]
        if (wd[wdName] && wd[wdName].items) {
          var items = wd[wdName].items
          var arr = Object.values(items)
          arr = filterIssuesItemsGlobally(arr, products, fm, fs, dr)
          var alertCount = 0
          for (var j = 0; j < arr.length; j++) {
            var item = arr[j]
            if (item.issueStatus === 0 && !item.muted && (item.status === 1 || item.status === 2)) {
              alertCount++
            }
          }
          var list = alertCount > 0 ? hasAlert : noAlert
          list.push(host.wdList[i])
        } else {
          noAlert.push(host.wdList[i])
        }
      }
      return hasAlert.concat(noAlert)
    },
    displayedWatchdogs: function () {
      if (!this.activeCategory) return this.wdListSorted
      if (this.wdListSorted.indexOf(this.activeCategory) >= 0) {
        return [this.activeCategory]
      }
      return this.wdListSorted
    },
    marketplaceColumnOptions: function () {
      var list = this.filterCountriesForDropdown || []
      var out = []
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i] !== 's') out.push(list[i])
      }
      return out
    },
    wdAlertCounts: function () {
      var host = this.issuesHost
      var wdList = (host && host.wdList) || []
      var wd = host && host.worldStore && host.worldStore.watchDogs
      var sac = this.summaryAlertCounts || {}
      var counts = {}
      for (var w = 0; w < wdList.length; w++) {
        var wdName = wdList[w]
        if (wd && wd[wdName] && wd[wdName].status === 21) {
          counts[wdName] = -1
          continue
        }
        var c = sac[wdName]
        if (!c) {
          counts[wdName] = 0
          continue
        }
        /** Badge total matches alert_counts API: active RED + AMBER (`yellow`) only. */
        counts[wdName] = (Number(c.red) || 0) + (Number(c.yellow) || 0)
      }
      return counts
    }
  },
  methods: {
    scrollToWatchdog: function (wdtype) {
      if (this.activeCategory === wdtype) {
        this.activeCategory = ''
        this.syncIssuesRouteQuery()
        return
      }
      this.activeCategory = wdtype
      this.syncIssuesRouteQuery()
      var self = this
      this.$nextTick(function () {
        if (!wdtype) {
          $('body,html').stop().animate({ scrollTop: 0 }, 300)
          return
        }
        var el = $('[data-wdtype=' + wdtype + ']')[0]
        if (!el) return
        var elY = 0
        for (var p = el; p && p.tagName !== 'BODY'; p = p.offsetParent) { elY += p.offsetTop }
        $('body,html').stop().animate({ scrollTop: Math.max(0, elY - 100) }, 300)
      })
    },

    fetchWatchdogsData: function () {
      var self = this
      if (self._issuesFetchDebounce) clearTimeout(self._issuesFetchDebounce)
      self._issuesFetchDebounce = setTimeout(function () {
        self._issuesFetchDebounce = null
        self.runIssuesSummaryFetch()
      }, 300)
    },

    /** Sidebar / URL category changes: GET summary/items only (badges already loaded). */
    scheduleIssuesSummaryItemsOnly: function () {
      var self = this
      if (self._issuesItemsFetchDebounce) clearTimeout(self._issuesItemsFetchDebounce)
      self._issuesItemsFetchDebounce = setTimeout(function () {
        self._issuesItemsFetchDebounce = null
        self.fetchIssuesSummaryItemsOnly()
      }, 100)
    },

    fetchIssuesSummaryItemsOnly: function () {
      var host = resolveIssuesHost(this)
      if (!host || !host.worldStore) return
      if (!this._issuesRequestGate) {
        this._issuesRequestGate = createRequestGate()
      }
      this._issuesRequestGate.begin()
      var types = computeWatchdogTypesForItemsFetch(this, host)
      fetchSummaryItemsForTypes(this, host, types, function () {})
    },

    /** Keep sidebar badges, tab totals, and lists aligned after resolve/mute mutations. */
    onIssuesSummaryRefreshRequest: function () {
      var host = resolveIssuesHost(this)
      if (!host || !host.worldStore) return
      refreshIssuesSummaryQuiet(this, host, function () {})
    },

    runIssuesSummaryFetch: function () {
      var self = this
      if (self._issuesSummaryFetchInFlight) return

      var host = resolveIssuesHost(self)
      if (!host || !host.worldStore) {
        console.error('[IssuesPage] No Issues host with worldStore (expected IndexPage ancestor)')
        self.error = 'Unable to load alerts'
        self.loading = false
        return
      }

      self.loading = true
      self.error = null
      self.issueProductsMap = {}
      self._issuesSummaryFetchInFlight = true

      runIssuesSummaryFetchCore(self, host, function () {
        self._issuesSummaryFetchInFlight = false
      })
    },

    updateTimeAgo: function () {
      if (!this.lastUpdateTime) {
        this.lastUpdated = 'just now'
        return
      }

      var now = Date.now()
      var diff = Math.floor((now - this.lastUpdateTime) / 1000)

      if (diff < 60) {
        this.lastUpdated = diff + 's ago'
      } else if (diff < 3600) {
        var minutes = Math.floor(diff / 60)
        this.lastUpdated = minutes + 'm ago'
      } else {
        var hours = Math.floor(diff / 3600)
        this.lastUpdated = hours + 'h ago'
      }
    },
    startTimer: function () {
      var self = this
      this.lastUpdateTime = Date.now()
      this.lastUpdated = 'just now'

      this.timerInterval = setInterval(function () {
        self.updateTimeAgo()
      }, 1000)
    },
    loadWatchdogsData: function () {
      this.fetchWatchdogsData()
    },
    updateFilters: function () {
      var ws = this.issuesWorldStore
      if (ws) {
        var marketplaces = []
        var addMp = function (mp) {
          if (mp == null || mp === '') return
          var m = String(mp).trim().toUpperCase()
          if (m === 'GB') m = 'UK'
          if (marketplaces.indexOf(m) < 0) marketplaces.push(m)
        }
        var products = ws.products || {}
        var productKeys = Object.keys(products)
        for (var i = 0; i < productKeys.length; i++) {
          var p = products[productKeys[i]]
          if (p && p.marketplace) addMp(p.marketplace)
        }
        var sellerMpFromApi = this.issuesSellerMarketplaceCodes || []
        for (var s = 0; s < sellerMpFromApi.length; s++) {
          addMp(sellerMpFromApi[s])
        }
        var canonicalOrder = this.filterCountriesAll.filter(function (c) { return c !== 's' })
        var canonicalSet = {}
        for (var k = 0; k < canonicalOrder.length; k++) canonicalSet[canonicalOrder[k]] = true
        var sorted = []
        for (var o = 0; o < canonicalOrder.length; o++) {
          if (marketplaces.indexOf(canonicalOrder[o]) >= 0) sorted.push(canonicalOrder[o])
        }
        for (var m = 0; m < marketplaces.length; m++) {
          if (!canonicalSet[marketplaces[m]] && sorted.indexOf(marketplaces[m]) < 0) {
            sorted.push(marketplaces[m])
          }
        }
        this.filterUserCountries = sorted
        if (this.filterByMarket.length > 0) {
          var ms = new Set(this.filterUserCountries)
          this.filterByMarket = this.filterByMarket.filter(function (x) { return ms.has(x) })
        }
        var v1 = this.issuesV1SellerRows
        var saCount = (v1 != null && v1.length > 0)
          ? v1.length
          : Object.keys(ws.sellerAccounts || {}).length
        this.filterShowSASelector = saCount > 1
        this.initDropdowns()
      }
    },
    loadIssuesV1Sellers: function () {
      var self = this
      if (self.issuesV1SellerRows !== null) return
      if (self._issuesV1SellersPromise) return
      var sellersPromise = self.$root.loadV1Sellers
        ? self.$root.loadV1Sellers()
        : self.$http.get('/api/v1/sellers').then(function (res) { return res.body || {} })
      self._issuesV1SellersPromise = sellersPromise.then(function (body) {
        body = body || {}
        self.issuesV1SellerRows = mapV1SellersToFilterAccounts(extractV1SellersItems(body))
        self.issuesSellerMarketplaceCodes = extractMarketplaceCodesFromV1SellersBody(body)
        self.updateFilters()
      }, function () {
        self.issuesV1SellerRows = []
        self.issuesSellerMarketplaceCodes = []
        self.updateFilters()
      }).then(function () {
        self._issuesV1SellersPromise = null
      }, function () {
        self._issuesV1SellersPromise = null
      })
    },
    /** Semantic / Fomantic multi dropdown may pass a string, array, or empty; only handling strings cleared filters. */
    parseDropdownMulti: function (v) {
      if (v === '' || v === null || v === undefined || v === false) return []
      if (Array.isArray(v)) return v.map(String).filter(function (s) { return s.length > 0 })
      if (typeof v === 'string') {
        if (!v.trim()) return []
        return v.split(',').map(function (s) { return s.trim() }).filter(Boolean)
      }
      return []
    },
    /** Fomantic multi dropdowns stay open after each pick; close so filters + loading UI aren't obscured. */
    hideIssuesNavbarDropdown: function (refKey) {
      this.forceCloseIssuesNavbarDropdown(refKey)
    },
    /**
     * Multi-selection dropdowns ignore a single `hide()` call; tear down visible state like outside-click.
     */
    forceCloseIssuesNavbarDropdown: function (refKey) {
      var self = this
      function doClose () {
        var root = self.$refs[refKey]
        if (!root) return
        var $el = $(root)
        try {
          $el.dropdown('hide')
        } catch (err) { /* destroy/re-init race */ }
        try {
          $el.removeClass('active')
          $el.find('.menu').removeClass('visible')
          $el.find('.menu.transition').removeClass('visible')
        } catch (e2) {}
        try {
          $el.find('input.search').blur()
        } catch (blErr) {}
        var active = document.activeElement
        if (active && root.contains(active)) {
          try {
            active.blur()
          } catch (e3) {}
        }
      }
      self.$nextTick(function () {
        doClose()
        setTimeout(doClose, 0)
        setTimeout(doClose, 10)
        setTimeout(doClose, 80)
      })
    },
    /** Multi dropdown keeps menu open by design; closing after item interaction matches Stock / Products UX. */
    bindIssuesMultiDropdownCloseOnPick: function (refKey) {
      var self = this
      var root = self.$refs[refKey]
      if (!root) return
      var $root = $(root)
      $root.off('mouseup.skIssuesDdClose touchend.skIssuesDdClose')
      function scheduleClose () {
        setTimeout(function () {
          self.forceCloseIssuesNavbarDropdown(refKey)
        }, 10)
      }
      $root.on('mouseup.skIssuesDdClose touchend.skIssuesDdClose', '.menu .item:not(.divider)', function (e) {
        if (e.target && e.target.closest && e.target.closest('.divider')) return
        scheduleClose()
      })
    },
    /** Imperative summary row — Vue must not bind inside `multiple` dropdown or chip remove / labels break. */
    syncIssuesNavbarDropdownSummaryTexts: function () {
      var self = this
      self.$nextTick(function () {
        if (self.$refs.filterC) {
          var el = $(self.$refs.filterC).find('.issues-navbar-dd-summary').first()
          if (el.length) el.text('Marketplaces: ' + self.marketplacesFilterLabel)
        }
        if (self.$refs.filterSA) {
          var el2 = $(self.$refs.filterSA).find('.issues-navbar-dd-summary').first()
          if (el2.length) el2.text('S.Accounts: ' + self.sellerAccountsFilterLabel)
        }
      })
    },
    initDropdowns: function () {
      var self = this
      self._silenceDropdownChange = true
      this.$nextTick(function () {
        if (self.$refs.filterC) {
          var $filterC = $(self.$refs.filterC)
          try {
            $filterC.off('mouseup.skIssuesDdClose touchend.skIssuesDdClose')
          } catch (eOff) {}
          try {
            $filterC.off('click.skIssuesLblDel')
          } catch (eOff2) {}
          try {
            $filterC.dropdown('destroy')
          } catch (e) {}
          $filterC.dropdown({
            fullTextSearch: true,
            onChange: function (v) {
              if (self._silenceDropdownChange) return
              setTimeout(function () {
                var raw = v
                try {
                  raw = $filterC.dropdown('get value')
                } catch (eGet) {}
                self.filterByMarket = self.parseDropdownMulti(raw)
                self.syncIssuesNavbarDropdownSummaryTexts()
                self.fetchWatchdogsData()
                self.syncIssuesRouteQuery()
              }, 0)
            }
          })
          self.bindIssuesMultiDropdownCloseOnPick('filterC')
        }
        if (self.$refs.filterSA) {
          var $filterSA = $(self.$refs.filterSA)
          try {
            $filterSA.off('mouseup.skIssuesDdClose touchend.skIssuesDdClose')
          } catch (eOffSa) {}
          try {
            $filterSA.off('click.skIssuesLblDel')
          } catch (eOffSa2) {}
          try {
            $filterSA.dropdown('destroy')
          } catch (e) {}
          $filterSA.dropdown({
            fullTextSearch: true,
            onChange: function (v) {
              if (self._silenceDropdownChange) return
              setTimeout(function () {
                var raw = v
                try {
                  raw = $filterSA.dropdown('get value')
                } catch (eGet) {}
                var vals = self.parseDropdownMulti(raw)
                self.filterBySellAcc = vals.map(function (x) {
                  var n = Number(x)
                  return isNaN(n) ? x : n
                })
                self.syncIssuesNavbarDropdownSummaryTexts()
                self.fetchWatchdogsData()
                self.syncIssuesRouteQuery()
              }, 0)
            }
          })
          self.bindIssuesMultiDropdownCloseOnPick('filterSA')
        }
        if (self.$refs.filterDate) {
          try { $(self.$refs.filterDate).dropdown('destroy') } catch (e) {}
          $(self.$refs.filterDate).dropdown({
            onChange: function (v) {
              if (self._silenceDropdownChange) return
              if (v === 'custom') {
                self.openCustomDateRange()
                self.hideIssuesNavbarDropdown('filterDate')
                return
              }
              self.showCustomDatePicker = false
              if (v) self.dateRange = v
              self.issuesListPage = 1
              self.fetchWatchdogsData()
              self.syncIssuesRouteQuery()
              self.hideIssuesNavbarDropdown('filterDate')
            }
          })
          var dr = self.dateRange === 'all time' ? 'all' : self.dateRange
          try {
            $(self.$refs.filterDate).dropdown('set selected', dr)
          } catch (e2) { /* ignore */ }
          self.syncCustomDateDropdownText()
        }
        try {
          if (self.$refs.filterC) {
            $(self.$refs.filterC).dropdown('set selected', (self.filterByMarket || []).map(String).join(',') || '')
          }
        } catch (efc) {}
        try {
          if (self.$refs.filterSA) {
            $(self.$refs.filterSA).dropdown('set selected', (self.filterBySellAcc || []).map(String).join(',') || '')
          }
        } catch (efsa) {}
        self.syncIssuesNavbarDropdownSummaryTexts()
        setTimeout(function () {
          self._silenceDropdownChange = false
        }, 0)
      })
    },
    scrollIssuesListToCategory: function (wdtype) {
      if (!wdtype) return
      this.$nextTick(function () {
        var el = $('[data-wdtype=' + wdtype + ']')[0]
        if (!el) return
        var elY = 0
        for (var p = el; p && p.tagName !== 'BODY'; p = p.offsetParent) { elY += p.offsetTop }
        $('body,html').stop().animate({ scrollTop: Math.max(0, elY - 100) }, 300)
      })
    },
    buildIssuesRouteQuery: function () {
      var o = {}
      if (!this.activeCategory) o.category = 'all'
      else o.category = this.activeCategory
      if (this.filterByMarket.length) o.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length) o.sellerAccounts = this.filterBySellAcc.map(String).join(',')
      if (this.dateRange && this.dateRange !== 'all') o.dateRange = this.dateRange
      if (this.dateRange === 'custom') {
        o.startDate = this.customDateStart
        o.endDate = this.customDateEnd
      }
      if (this.issuesListTab && this.issuesListTab !== 'active') o.tab = this.issuesListTab
      if (this.highlightedAlertId) o.alertId = this.highlightedAlertId
      return o
    },
    syncIssuesRouteQuery: function () {
      if (this._applyingIssuesRouteQuery || this.$route.path !== '/issues') return
      var next = this.buildIssuesRouteQuery()
      if (shallowEqualIssuesQuery(next, this.$route.query)) return
      this.$router.replace({ path: '/issues', query: next })
    },
    applyIssuesRouteQuery: function (q) {
      if (!q || typeof q !== 'object') return false
      var has = false
      for (var i = 0; i < ISSUES_URL_KEYS.length; i++) {
        var k = ISSUES_URL_KEYS[i]
        if (q[k] != null && q[k] !== '') {
          has = true
          break
        }
      }
      if (!has && (q.type || q.id || q.watchDogId || q.watchdog_id)) has = true
      if (!has) return false

      this._applyingIssuesRouteQuery = true
      try {
        if (!Object.prototype.hasOwnProperty.call(q, 'category')) this.activeCategory = 'lostBuybox'
        if (!Object.prototype.hasOwnProperty.call(q, 'marketplaces')) this.filterByMarket = []
        if (!Object.prototype.hasOwnProperty.call(q, 'sellerAccounts')) this.filterBySellAcc = []
        if (!Object.prototype.hasOwnProperty.call(q, 'dateRange')) this.dateRange = 'all'
        if (!Object.prototype.hasOwnProperty.call(q, 'tab')) this.issuesListTab = 'active'

        if (Object.prototype.hasOwnProperty.call(q, 'category')) {
          var catRaw = q.category
          if (Array.isArray(catRaw)) catRaw = catRaw[0]
          var catStr = catRaw != null ? String(catRaw).trim() : ''
          if (!catStr || catStr === 'all') this.activeCategory = ''
          else this.activeCategory = catStr
        } else if (q.type) {
          var fromType = categoryFromAlertEmailType(q.type)
          if (fromType) this.activeCategory = fromType
        }
        var deepId = alertIdFromQuery(q)
        this.highlightedAlertId = deepId
        this.issuesFocusPending = !!deepId
        if (Object.prototype.hasOwnProperty.call(q, 'marketplaces') && q.marketplaces) {
          this.filterByMarket = String(q.marketplaces).split(',').map(function (s) { return s.trim() }).filter(Boolean)
        }
        if (Object.prototype.hasOwnProperty.call(q, 'sellerAccounts') && q.sellerAccounts) {
          this.filterBySellAcc = String(q.sellerAccounts).split(',').map(function (s) {
            var t = s.trim()
            if (!t) return null
            var n = Number(t)
            return isNaN(n) ? t : n
          }).filter(function (id) { return id != null && id !== '' })
        }
        if (Object.prototype.hasOwnProperty.call(q, 'dateRange') && q.dateRange) this.dateRange = q.dateRange
        if (this.dateRange === 'custom') {
          if (isValidCustomRange(q.startDate, q.endDate)) {
            this.customDateStart = String(q.startDate)
            this.customDateEnd = String(q.endDate)
          } else {
            this.dateRange = 'all'
          }
        }
        if ((q.tab === 'active' || q.tab === 'resolved') && !(this.issuesResolvedHidden && q.tab === 'resolved')) {
          this.issuesListTab = q.tab
        } else if (this.issuesResolvedHidden) {
          this.issuesListTab = 'active'
        }

        if (Object.prototype.hasOwnProperty.call(q, 'category')) {
          var cRaw = q.category
          if (Array.isArray(cRaw)) cRaw = cRaw[0]
          var c = cRaw != null ? String(cRaw).trim() : ''
          this._pendingIssuesScrollCategory = c && c !== 'all' ? c : null
        } else if (this.activeCategory) {
          this._pendingIssuesScrollCategory = this.activeCategory
        }
      } finally {
        var self = this
        this.$nextTick(function () {
          self._applyingIssuesRouteQuery = false
        })
      }
      return true
    },
    onIssuesTabChange: function (tab) {
      if (this.issuesResolvedHidden && tab === 'resolved') return
      if (this.issuesListTab === tab) return
      this.issuesListTab = tab
      this.issuesListPage = 1
      this.issuesFocusPending = false
      this.syncIssuesRouteQuery()
      this.scheduleIssuesSummaryItemsOnly()
    },

    /** Instant sidebar badge update from set-issue-status `alert_counts_delta`. */
    onAlertCountsDelta: function (delta) {
      if (!delta || !delta.watchdog_type) return
      var key = watchdogTypeFromApi(delta.watchdog_type)
      if (!key) return
      var cur = (this.summaryAlertCounts && this.summaryAlertCounts[key]) || { red: 0, yellow: 0 }
      var next = {
        red: Math.max(0, (cur.red || 0) + (Number(delta.red) || 0)),
        yellow: Math.max(0, (cur.yellow || 0) + (Number(delta.yellow) || 0))
      }
      this.$set(this.summaryAlertCounts, key, next)
      this.$forceUpdate()
    },
    onIssuesPageChange: function (page) {
      var next = parseInt(page, 10)
      if (!next || next < 1 || next === this.issuesListPage) return
      this.issuesListPage = next
      this.issuesFocusPending = false
      this.scheduleIssuesSummaryItemsOnly()
    },
    onIssuesFeeDirectionChange: function (direction) {
      var next = direction === 'increase' || direction === 'decrease' ? direction : ''
      if (next === this.issuesFeeDirection) return
      this.issuesFeeDirection = next
      this.issuesListPage = 1
      this.issuesFocusPending = false
      this.scheduleIssuesSummaryItemsOnly()
    },
    openCustomDateRange: function () {
      this.customDraftStart = this.customDateStart || isoDateDaysAgo(29)
      this.customDraftEnd = this.customDateEnd || isoDateDaysAgo(0)
      this.showCustomDatePicker = true
    },
    applyCustomDateRange: function () {
      if (!this.customDraftValid) return
      this.customDateStart = this.customDraftStart
      this.customDateEnd = this.customDraftEnd
      this.dateRange = 'custom'
      this.showCustomDatePicker = false
      this.syncCustomDateDropdownText()
      this.issuesListPage = 1
      this.fetchWatchdogsData()
      this.syncIssuesRouteQuery()
    },
    /** Semantic dropdown shows the item text ("Custom range…"); show the applied dates instead. */
    syncCustomDateDropdownText: function () {
      if (this.dateRange !== 'custom' || !this.$refs.filterDate) return
      try {
        $(this.$refs.filterDate).dropdown('set text', 'Date Range: ' + this.dateRangeLabel)
      } catch (e) {}
    },
    /** Dropdown already shows "Custom range…"; put it back on the range that is actually applied. */
    cancelCustomDateRange: function () {
      this.showCustomDatePicker = false
      var self = this
      self._silenceDropdownChange = true
      try {
        $(self.$refs.filterDate).dropdown('set selected', self.dateRange === 'all time' ? 'all' : self.dateRange)
      } catch (e) {}
      self.syncCustomDateDropdownText()
      self.$nextTick(function () {
        self._silenceDropdownChange = false
      })
    },
    onIssuesSearchChange: function (query) {
      var next = query == null ? '' : String(query)
      if (next === this.issuesSearchQuery) return
      this.issuesSearchQuery = next
      this.issuesListPage = 1
      this.issuesFocusPending = false
      var self = this
      if (self._issuesSearchDebounce) clearTimeout(self._issuesSearchDebounce)
      self._issuesSearchDebounce = setTimeout(function () {
        self._issuesSearchDebounce = null
        self.scheduleIssuesSummaryItemsOnly()
      }, 300)
    },
    onMarketplaceColumnFilter: function (codes) {
      var next = (codes || []).map(normalizeMarketplaceCode).filter(Boolean)
      this.filterByMarket = next
      this._silenceDropdownChange = true
      try {
        if (this.$refs.filterC) {
          $(this.$refs.filterC).dropdown('set selected', next.join(',') || '')
        }
      } catch (e) {}
      this.syncIssuesNavbarDropdownSummaryTexts()
      var self = this
      this.$nextTick(function () {
        self._silenceDropdownChange = false
      })
      this.fetchWatchdogsData()
      this.syncIssuesRouteQuery()
    },
    tryFocusDeepLinkedAlert: function () {
      var id = this.highlightedAlertId
      if (!id) return
      var found = this.findAlertAcrossWatchdogs(id)
      if (found) {
        if (found.wdtype && this.activeCategory !== found.wdtype) {
          this.activeCategory = found.wdtype
          this.syncIssuesRouteQuery()
        }
        var onResolved = isIssuesResolvedTabItem(found.item)
        var onActive = isIssuesActiveTabItem(found.item)
        if (onResolved && !this.issuesResolvedHidden && this.issuesListTab !== 'resolved') {
          this.issuesListTab = 'resolved'
          this.syncIssuesRouteQuery()
        } else if ((onActive || this.issuesResolvedHidden) && this.issuesListTab !== 'active') {
          this.issuesListTab = 'active'
          this.syncIssuesRouteQuery()
        }
      }
      this.$nextTick(function () {
        var el = document.querySelector('[data-alert-id="' + String(id).replace(/"/g, '') + '"]')
        if (!el) return
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
          return
        }
        var elY = 0
        for (var p = el; p && p.tagName !== 'BODY'; p = p.offsetParent) { elY += p.offsetTop }
        $('body,html').stop().animate({ scrollTop: Math.max(0, elY - 120) }, 300)
      })
    },
    findAlertAcrossWatchdogs: function (alertId) {
      var ws = this.issuesWorldStore
      if (!ws || !ws.watchDogs) return null
      var types = Object.keys(ws.watchDogs)
      for (var i = 0; i < types.length; i++) {
        var wd = ws.watchDogs[types[i]]
        var items = wd && wd.items ? Object.values(wd.items) : []
        for (var j = 0; j < items.length; j++) {
          if (itemMatchesAlertId(items[j], alertId)) {
            return { wdtype: types[i], item: items[j] }
          }
        }
      }
      return null
    }
  },
  watch: {
    '$route': function (to, from) {
      if (to.path !== '/issues') return
      if (this._applyingIssuesRouteQuery) return
      /** Compare previous vs next URL — not `buildIssuesRouteQuery()`, because sidebar updates vm before navigate and would incorrectly skip. */
      if (from && from.path === to.path && shallowEqualIssuesQuery(from.query, to.query)) return

      var prevQ = from && from.query ? from.query : {}
      var nextQ = to.query || {}
      var catOnly = !!(from && from.path === to.path && categoryQueryOnlyChange(prevQ, nextQ))
      var countsReady = Object.keys(this.summaryAlertCounts || {}).length > 0

      this.applyIssuesRouteQuery(to.query)
      if (catOnly && countsReady) {
        this.issuesListPage = 1
        this.issuesFocusPending = false
        this.scheduleIssuesSummaryItemsOnly()
      } else {
        this.fetchWatchdogsData()
      }
    },
    sellerAccountsList: function () {
      this.$nextTick(this.initDropdowns)
    }
  },
  created: function () {
    this.applyIssuesRouteQuery(this.$route.query)
  },
  mounted: function () {
    this.startTimer()
    this.loadIssuesV1Sellers()
    this.updateFilters()
    this.initDropdowns()
    this.loadWatchdogsData()
    var self = this
    this.$nextTick(function () {
      self.syncIssuesRouteQuery()
    })
  },
  beforeDestroy: function () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
    if (this._issuesFetchDebounce) {
      clearTimeout(this._issuesFetchDebounce)
      this._issuesFetchDebounce = null
    }
    if (this._issuesItemsFetchDebounce) {
      clearTimeout(this._issuesItemsFetchDebounce)
      this._issuesItemsFetchDebounce = null
    }
  }
}
</script>




// WEBPACK FOOTER //
// IssuesPage.vue?13aa8895